import React, { useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import { Avatar, AvatarProps, ClassNameMap } from '@mui/material';
import StickyNote2SharpIcon from '@mui/icons-material/StickyNote2Sharp';
import { TimeSignalSource } from 'graphql/types';
import config from 'config';

const useStyles = makeStyles((theme) => {
  const avatarSize = 6;
  const imgSize = 3;
  return {
    single: {
      padding: theme.spacing(1)
    },
    root: {
      flexDirection: 'row',
      width: theme.spacing(avatarSize),
      height: theme.spacing(avatarSize),
      borderRadius: '50%',
      border: `2px solid ${theme.palette.background.paper}`
    },
    rootWithSubAvatar: {
      backgroundColor: theme.palette.background.paper
    },
    container: {
      flex: '1 1 50%',
      display: 'flex',
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center',
      alignContent: 'center',
      backgroundColor: theme.palette.background.paper
    },
    left: {
      margin: theme.spacing(0, 1 / 8, 0, 0),
      flexDirection: 'column'
    },
    right: {
      margin: theme.spacing(0, 0, 0, 1 / 8),
      flexDirection: 'column'
    },
    top: {
      margin: theme.spacing(0, 0, 1 / 8, 0)
    },
    bottom: {
      margin: theme.spacing(1 / 8, 0, 0, 0)
    },
    subAvatar: {
      width: theme.spacing(avatarSize),
      height: theme.spacing(avatarSize),
      fontSize: theme.spacing(avatarSize / 2.8)
    },
    subAvatarSmall: {
      width: theme.spacing(avatarSize / 2.5),
      height: theme.spacing(avatarSize / 2.5),
      fontSize: theme.spacing(avatarSize / 4),
      backgroundColor: theme.palette.common.white
    },
    moreAvatar: {
      width: theme.spacing(avatarSize / 2),
      height: theme.spacing(avatarSize / 2.5),
      backgroundColor: theme.palette.background.paper,
      fontSize: theme.spacing(avatarSize / 4),
      color: theme.palette.text.primary,
      padding: theme.spacing(0, 0.5, 0.5, 0)
    },
    empty: {
      background: theme.palette.common.white,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    emptyIcon: {
      color: theme.palette.noteListIcon.empty,
      width: theme.spacing(imgSize),
      height: theme.spacing(imgSize)
    },
    img: {
      display: 'flex',
      width: theme.spacing(imgSize),
      height: theme.spacing(imgSize)
    }
  };
});

export const useTIWIconStyles = makeStyles((theme) => ({
  tiwIcon: {
    color: theme.palette.noteListIcon.empty,
    width: theme.spacing(3),
    height: theme.spacing(3)
  },
  tiwAvatar: {
    backgroundColor: theme.palette.common.white
  }
}));

type NoteSourceGroupProp = {
  className?: string;
  sources: TimeSignalSource[];
  classes?: ClassNameMap;
};

export const NoteSourceGroup: React.FC<NoteSourceGroupProp> = ({
  className,
  sources,
  classes: overrideClasses
}: NoteSourceGroupProp) => {
  const classes = useStyles({ classes: overrideClasses });
  const avatarClasses = useMemo(
    () => ({
      root: classnames(className, classes.root, classes.single),
      img: classes.img
    }),
    [className, classes.img, classes.root, classes.single]
  );
  if (!sources.length) {
    return (
      <div
        className={classnames(classes.root, classes.empty, className)}
        data-testid={'empty-note-sources'}
      >
        <StickyNote2SharpIcon className={classes.emptyIcon} />
      </div>
    );
  }

  if (1 === sources.length) {
    return (
      <SubAvatar
        data-testid={'note-sources-1'}
        classes={avatarClasses}
        source={sources[0]}
      />
    );
  }

  return (
    <Avatar
      className={classnames(
        classes.root,
        { [classes.rootWithSubAvatar]: sources.length },
        className
      )}
    >
      {sources.length && (
        <>
          <div className={classnames(classes.container, classes.left)}>
            {sources.length > 2 ? (
              <>
                <div className={classnames(classes.container, classes.top)}>
                  <SubAvatar
                    data-testid={'note-sources-1'}
                    className={classnames(
                      classes.subAvatar,
                      classes.subAvatarSmall
                    )}
                    source={sources[0]}
                  />
                </div>
                <div className={classnames(classes.container, classes.bottom)}>
                  <SubAvatar
                    data-testid={'note-sources-3'}
                    className={classnames(
                      classes.subAvatar,
                      classes.subAvatarSmall
                    )}
                    source={sources[2]}
                  />
                </div>
              </>
            ) : (
              <SubAvatar
                data-testid={'note-sources-1'}
                source={sources[0]}
                className={classes.subAvatarSmall}
              />
            )}
          </div>
          <div className={classnames(classes.container, classes.right)}>
            {sources.length > 3 ? (
              <>
                <div className={classnames(classes.container, classes.top)}>
                  <SubAvatar
                    data-testid={'note-sources-2'}
                    className={classnames(
                      classes.subAvatar,
                      classes.subAvatarSmall
                    )}
                    source={sources[1]}
                  />
                </div>
                <div className={classnames(classes.container, classes.bottom)}>
                  {sources.length > 4 ? (
                    <>
                      <Avatar
                        data-testid={'note-sources-more'}
                        className={classnames(
                          classes.subAvatar,
                          classes.moreAvatar
                        )}
                        variant="square"
                      >
                        +{sources.length - 3}
                      </Avatar>
                    </>
                  ) : (
                    <>
                      <SubAvatar
                        data-testid={'note-sources-4'}
                        className={classnames(
                          classes.subAvatar,
                          classes.subAvatarSmall
                        )}
                        source={sources[3]}
                      />
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <SubAvatar
                  data-testid={'note-sources-2'}
                  source={sources[1]}
                  className={classes.subAvatarSmall}
                />
              </>
            )}
          </div>
        </>
      )}
    </Avatar>
  );
};

type SubAvatarProps = AvatarProps & {
  className?: string;
  source: TimeSignalSource;
};
const SubAvatar: React.FC<SubAvatarProps> = ({
  source,
  ...rest
}: SubAvatarProps) => {
  const classes = useTIWIconStyles();

  // source is TIW, ie user manually added note
  if (source.id === config.clientId) {
    return (
      <Avatar
        alt={source.name}
        variant="square"
        className={classes.tiwAvatar}
        {...rest}
      >
        <StickyNote2SharpIcon className={classes.tiwIcon} />
      </Avatar>
    );
  }

  return (
    <Avatar
      alt={source.name}
      src={source.image?.src}
      variant="square"
      className={classes.tiwAvatar}
      {...rest}
    />
  );
};
